import { getSettings } from '@wordpress/date';
import { enUS } from 'date-fns/locale/en-US';
import type { Locale } from 'date-fns';

/**
 * Cache for loaded locales to avoid re-importing
 */
const localeCache = new Map<string, Locale>();

/**
 * Map WordPress locale codes to date-fns locale module names
 *
 * WordPress uses underscore format (e.g., 'en_US', 'es_ES')
 * date-fns uses hyphen format (e.g., 'en-US', 'es-ES') or short codes (e.g., 'es', 'fr')
 */
const LOCALE_MAP: Record<string, string> = {
	// English variants
	en_US: 'en-US',
	en_GB: 'en-GB',
	en_CA: 'en-CA',
	en_AU: 'en-AU',
	en_NZ: 'en-NZ',
	en_ZA: 'en-ZA',
	en_IE: 'en-IE',
	en_IN: 'en-IN',

	// Spanish variants
	es_ES: 'es',
	es_MX: 'es',
	es_AR: 'es',
	es_CO: 'es',
	es_CL: 'es',
	es_PE: 'es',
	es_VE: 'es',

	// French variants
	fr_FR: 'fr',
	fr_CA: 'fr-CA',
	fr_BE: 'fr',
	fr_CH: 'fr-CH',

	// German variants
	de_DE: 'de',
	de_AT: 'de-AT',
	de_CH: 'de',

	// Italian variants
	it_IT: 'it',
	it_CH: 'it-CH',

	// Portuguese variants
	pt_PT: 'pt',
	pt_BR: 'pt-BR',

	// Dutch variants
	nl_NL: 'nl',
	nl_BE: 'nl-BE',

	// Other European languages
	pl_PL: 'pl',
	ru_RU: 'ru',
	uk: 'uk',
	cs_CZ: 'cs',
	sk_SK: 'sk',
	hu_HU: 'hu',
	ro_RO: 'ro',
	bg_BG: 'bg',
	hr: 'hr',
	sl_SI: 'sl',
	sr_RS: 'sr',
	el: 'el',
	tr_TR: 'tr',

	// Nordic languages
	sv_SE: 'sv',
	da_DK: 'da',
	nb_NO: 'nb',
	nn_NO: 'nn',
	fi: 'fi',
	is_IS: 'is',

	// Asian languages
	ja: 'ja',
	ko_KR: 'ko',
	zh_CN: 'zh-CN',
	zh_TW: 'zh-TW',
	zh_HK: 'zh-HK',
	th: 'th',
	vi: 'vi',
	id_ID: 'id',
	ms_MY: 'ms',

	// Middle Eastern languages
	ar: 'ar',
	ar_SA: 'ar-SA',
	ar_EG: 'ar-EG',
	ar_MA: 'ar-MA',
	ar_DZ: 'ar-DZ',
	ar_TN: 'ar-TN',
	he_IL: 'he',
	fa_IR: 'fa-IR',

	// Indian languages
	hi_IN: 'hi',
	bn_BD: 'bn',
	ta_IN: 'ta',
	te_IN: 'te',
	gu: 'gu',
	kn: 'kn',

	// Other languages
	ca: 'ca',
	eu: 'eu',
	gl_ES: 'gl',
	cy: 'cy',
	ga: 'gd',
	sq: 'sq',
	mk_MK: 'mk',
	bs_BA: 'bs',
	et: 'et',
	lv: 'lv',
	lt_LT: 'lt',
	ka_GE: 'ka',
	hy: 'hy',
	az: 'az',
	kk: 'kk',
	uz_UZ: 'uz',
	mn: 'mn',
	km: 'km',
	ckb: 'ckb',
	ug_CN: 'ug',
};

/**
 * Get the date-fns locale module name from WordPress locale code
 *
 * @param wpLocale WordPress locale code (e.g., 'en_US', 'es_ES')
 * @return date-fns locale module name (e.g., 'en-US', 'es')
 */
function getDateFnsLocaleName(wpLocale: string): string {
	// Check direct mapping first
	if (LOCALE_MAP[wpLocale]) {
		return LOCALE_MAP[wpLocale];
	}

	// Try base language code (e.g., 'es_MX' -> 'es')
	const baseLanguage = wpLocale.split('_')[0];
	if (LOCALE_MAP[baseLanguage]) {
		return LOCALE_MAP[baseLanguage];
	}

	// Try converting underscore to hyphen (e.g., 'en_US' -> 'en-US')
	const hyphenated = wpLocale.replace('_', '-');
	if (LOCALE_MAP[hyphenated]) {
		return LOCALE_MAP[hyphenated];
	}

	// Fallback to base language
	return baseLanguage;
}

/**
 * Dynamically import a date-fns locale directly from date-fns package
 *
 * @param localeName date-fns locale module name (e.g., 'es', 'en-GB')
 * @return Promise resolving to the locale object
 */
async function importLocale(localeName: string): Promise<Locale> {
	try {
		// Use a switch statement for webpack to properly code-split
		// This creates individual chunks for each locale
		let localeModule;

		switch (localeName) {
			case 'en-GB': // English (United Kingdom)
				localeModule = await import(
					/* webpackChunkName: "locale-en-GB" */ 'date-fns/locale/en-GB'
				);
				break;
			case 'en-CA': // English (Canada)
				localeModule = await import(
					/* webpackChunkName: "locale-en-CA" */ 'date-fns/locale/en-CA'
				);
				break;
			case 'en-AU': // English (Australia)
				localeModule = await import(
					/* webpackChunkName: "locale-en-AU" */ 'date-fns/locale/en-AU'
				);
				break;
			case 'en-NZ': // English (New Zealand)
				localeModule = await import(
					/* webpackChunkName: "locale-en-NZ" */ 'date-fns/locale/en-NZ'
				);
				break;
			case 'en-ZA': // English (South Africa)
				localeModule = await import(
					/* webpackChunkName: "locale-en-ZA" */ 'date-fns/locale/en-ZA'
				);
				break;
			case 'en-IE': // English (Ireland)
				localeModule = await import(
					/* webpackChunkName: "locale-en-IE" */ 'date-fns/locale/en-IE'
				);
				break;
			case 'en-IN': // English (India)
				localeModule = await import(
					/* webpackChunkName: "locale-en-IN" */ 'date-fns/locale/en-IN'
				);
				break;
			case 'es': // Spanish
				localeModule = await import(
					/* webpackChunkName: "locale-es" */ 'date-fns/locale/es'
				);
				break;
			case 'fr': // French
				localeModule = await import(
					/* webpackChunkName: "locale-fr" */ 'date-fns/locale/fr'
				);
				break;
			case 'fr-CA': // French (Canada)
				localeModule = await import(
					/* webpackChunkName: "locale-fr-CA" */ 'date-fns/locale/fr-CA'
				);
				break;
			case 'fr-CH': // French (Switzerland)
				localeModule = await import(
					/* webpackChunkName: "locale-fr-CH" */ 'date-fns/locale/fr-CH'
				);
				break;
			case 'de': // German
				localeModule = await import(
					/* webpackChunkName: "locale-de" */ 'date-fns/locale/de'
				);
				break;
			case 'de-AT': // German (Austria)
				localeModule = await import(
					/* webpackChunkName: "locale-de-AT" */ 'date-fns/locale/de-AT'
				);
				break;
			case 'it': // Italian
				localeModule = await import(
					/* webpackChunkName: "locale-it" */ 'date-fns/locale/it'
				);
				break;
			case 'it-CH': // Italian (Switzerland)
				localeModule = await import(
					/* webpackChunkName: "locale-it-CH" */ 'date-fns/locale/it-CH'
				);
				break;
			case 'pt': // Portuguese
				localeModule = await import(
					/* webpackChunkName: "locale-pt" */ 'date-fns/locale/pt'
				);
				break;
			case 'pt-BR': // Portuguese (Brazil)
				localeModule = await import(
					/* webpackChunkName: "locale-pt-BR" */ 'date-fns/locale/pt-BR'
				);
				break;
			case 'nl': // Dutch
				localeModule = await import(
					/* webpackChunkName: "locale-nl" */ 'date-fns/locale/nl'
				);
				break;
			case 'nl-BE': // Dutch (Belgium)
				localeModule = await import(
					/* webpackChunkName: "locale-nl-BE" */ 'date-fns/locale/nl-BE'
				);
				break;
			case 'pl': // Polish
				localeModule = await import(
					/* webpackChunkName: "locale-pl" */ 'date-fns/locale/pl'
				);
				break;
			case 'ru': // Russian
				localeModule = await import(
					/* webpackChunkName: "locale-ru" */ 'date-fns/locale/ru'
				);
				break;
			case 'uk': // Ukrainian
				localeModule = await import(
					/* webpackChunkName: "locale-uk" */ 'date-fns/locale/uk'
				);
				break;
			case 'cs': // Czech
				localeModule = await import(
					/* webpackChunkName: "locale-cs" */ 'date-fns/locale/cs'
				);
				break;
			case 'sk': // Slovak
				localeModule = await import(
					/* webpackChunkName: "locale-sk" */ 'date-fns/locale/sk'
				);
				break;
			case 'hu': // Hungarian
				localeModule = await import(
					/* webpackChunkName: "locale-hu" */ 'date-fns/locale/hu'
				);
				break;
			case 'ro': // Romanian
				localeModule = await import(
					/* webpackChunkName: "locale-ro" */ 'date-fns/locale/ro'
				);
				break;
			case 'bg': // Bulgarian
				localeModule = await import(
					/* webpackChunkName: "locale-bg" */ 'date-fns/locale/bg'
				);
				break;
			case 'hr': // Croatian
				localeModule = await import(
					/* webpackChunkName: "locale-hr" */ 'date-fns/locale/hr'
				);
				break;
			case 'sl': // Slovenian
				localeModule = await import(
					/* webpackChunkName: "locale-sl" */ 'date-fns/locale/sl'
				);
				break;
			case 'sr': // Serbian
				localeModule = await import(
					/* webpackChunkName: "locale-sr" */ 'date-fns/locale/sr'
				);
				break;
			case 'el': // Greek
				localeModule = await import(
					/* webpackChunkName: "locale-el" */ 'date-fns/locale/el'
				);
				break;
			case 'tr': // Turkish
				localeModule = await import(
					/* webpackChunkName: "locale-tr" */ 'date-fns/locale/tr'
				);
				break;
			case 'sv': // Swedish
				localeModule = await import(
					/* webpackChunkName: "locale-sv" */ 'date-fns/locale/sv'
				);
				break;
			case 'da': // Danish
				localeModule = await import(
					/* webpackChunkName: "locale-da" */ 'date-fns/locale/da'
				);
				break;
			case 'nb': // Norwegian Bokmål
				localeModule = await import(
					/* webpackChunkName: "locale-nb" */ 'date-fns/locale/nb'
				);
				break;
			case 'nn': // Norwegian Nynorsk
				localeModule = await import(
					/* webpackChunkName: "locale-nn" */ 'date-fns/locale/nn'
				);
				break;
			case 'fi': // Finnish
				localeModule = await import(
					/* webpackChunkName: "locale-fi" */ 'date-fns/locale/fi'
				);
				break;
			case 'is': // Icelandic
				localeModule = await import(
					/* webpackChunkName: "locale-is" */ 'date-fns/locale/is'
				);
				break;
			case 'ja': // Japanese
				localeModule = await import(
					/* webpackChunkName: "locale-ja" */ 'date-fns/locale/ja'
				);
				break;
			case 'ko': // Korean
				localeModule = await import(
					/* webpackChunkName: "locale-ko" */ 'date-fns/locale/ko'
				);
				break;
			case 'zh-CN': // Chinese (Simplified)
				localeModule = await import(
					/* webpackChunkName: "locale-zh-CN" */ 'date-fns/locale/zh-CN'
				);
				break;
			case 'zh-TW': // Chinese (Traditional, Taiwan)
				localeModule = await import(
					/* webpackChunkName: "locale-zh-TW" */ 'date-fns/locale/zh-TW'
				);
				break;
			case 'zh-HK': // Chinese (Traditional, Hong Kong)
				localeModule = await import(
					/* webpackChunkName: "locale-zh-HK" */ 'date-fns/locale/zh-HK'
				);
				break;
			case 'th': // Thai
				localeModule = await import(
					/* webpackChunkName: "locale-th" */ 'date-fns/locale/th'
				);
				break;
			case 'vi': // Vietnamese
				localeModule = await import(
					/* webpackChunkName: "locale-vi" */ 'date-fns/locale/vi'
				);
				break;
			case 'id': // Indonesian
				localeModule = await import(
					/* webpackChunkName: "locale-id" */ 'date-fns/locale/id'
				);
				break;
			case 'ms': // Malay
				localeModule = await import(
					/* webpackChunkName: "locale-ms" */ 'date-fns/locale/ms'
				);
				break;
			case 'hi': // Hindi
				localeModule = await import(
					/* webpackChunkName: "locale-hi" */ 'date-fns/locale/hi'
				);
				break;
			case 'bn': // Bengali
				localeModule = await import(
					/* webpackChunkName: "locale-bn" */ 'date-fns/locale/bn'
				);
				break;
			case 'ta': // Tamil
				localeModule = await import(
					/* webpackChunkName: "locale-ta" */ 'date-fns/locale/ta'
				);
				break;
			case 'te': // Telugu
				localeModule = await import(
					/* webpackChunkName: "locale-te" */ 'date-fns/locale/te'
				);
				break;
			case 'gu': // Gujarati
				localeModule = await import(
					/* webpackChunkName: "locale-gu" */ 'date-fns/locale/gu'
				);
				break;
			case 'kn': // Kannada
				localeModule = await import(
					/* webpackChunkName: "locale-kn" */ 'date-fns/locale/kn'
				);
				break;
			case 'ar': // Arabic
				localeModule = await import(
					/* webpackChunkName: "locale-ar" */ 'date-fns/locale/ar'
				);
				break;
			case 'ar-SA': // Arabic (Saudi Arabia)
				localeModule = await import(
					/* webpackChunkName: "locale-ar-SA" */ 'date-fns/locale/ar-SA'
				);
				break;
			case 'ar-EG': // Arabic (Egypt)
				localeModule = await import(
					/* webpackChunkName: "locale-ar-EG" */ 'date-fns/locale/ar-EG'
				);
				break;
			case 'ar-MA': // Arabic (Morocco)
				localeModule = await import(
					/* webpackChunkName: "locale-ar-MA" */ 'date-fns/locale/ar-MA'
				);
				break;
			case 'ar-DZ': // Arabic (Algeria)
				localeModule = await import(
					/* webpackChunkName: "locale-ar-DZ" */ 'date-fns/locale/ar-DZ'
				);
				break;
			case 'ar-TN': // Arabic (Tunisia)
				localeModule = await import(
					/* webpackChunkName: "locale-ar-TN" */ 'date-fns/locale/ar-TN'
				);
				break;
			case 'he': // Hebrew
				localeModule = await import(
					/* webpackChunkName: "locale-he" */ 'date-fns/locale/he'
				);
				break;
			case 'fa-IR': // Persian (Iran)
				localeModule = await import(
					/* webpackChunkName: "locale-fa-IR" */ 'date-fns/locale/fa-IR'
				);
				break;
			case 'ca': // Catalan
				localeModule = await import(
					/* webpackChunkName: "locale-ca" */ 'date-fns/locale/ca'
				);
				break;
			case 'eu': // Basque
				localeModule = await import(
					/* webpackChunkName: "locale-eu" */ 'date-fns/locale/eu'
				);
				break;
			case 'gl': // Galician
				localeModule = await import(
					/* webpackChunkName: "locale-gl" */ 'date-fns/locale/gl'
				);
				break;
			case 'cy': // Welsh
				localeModule = await import(
					/* webpackChunkName: "locale-cy" */ 'date-fns/locale/cy'
				);
				break;
			case 'gd': // Scottish Gaelic
				localeModule = await import(
					/* webpackChunkName: "locale-gd" */ 'date-fns/locale/gd'
				);
				break;
			case 'sq': // Albanian
				localeModule = await import(
					/* webpackChunkName: "locale-sq" */ 'date-fns/locale/sq'
				);
				break;
			case 'mk': // Macedonian
				localeModule = await import(
					/* webpackChunkName: "locale-mk" */ 'date-fns/locale/mk'
				);
				break;
			case 'bs': // Bosnian
				localeModule = await import(
					/* webpackChunkName: "locale-bs" */ 'date-fns/locale/bs'
				);
				break;
			case 'et': // Estonian
				localeModule = await import(
					/* webpackChunkName: "locale-et" */ 'date-fns/locale/et'
				);
				break;
			case 'lv': // Latvian
				localeModule = await import(
					/* webpackChunkName: "locale-lv" */ 'date-fns/locale/lv'
				);
				break;
			case 'lt': // Lithuanian
				localeModule = await import(
					/* webpackChunkName: "locale-lt" */ 'date-fns/locale/lt'
				);
				break;
			case 'ka': // Georgian
				localeModule = await import(
					/* webpackChunkName: "locale-ka" */ 'date-fns/locale/ka'
				);
				break;
			case 'hy': // Armenian
				localeModule = await import(
					/* webpackChunkName: "locale-hy" */ 'date-fns/locale/hy'
				);
				break;
			case 'az': // Azerbaijani
				localeModule = await import(
					/* webpackChunkName: "locale-az" */ 'date-fns/locale/az'
				);
				break;
			case 'kk': // Kazakh
				localeModule = await import(
					/* webpackChunkName: "locale-kk" */ 'date-fns/locale/kk'
				);
				break;
			case 'uz': // Uzbek
				localeModule = await import(
					/* webpackChunkName: "locale-uz" */ 'date-fns/locale/uz'
				);
				break;
			case 'mn': // Mongolian
				localeModule = await import(
					/* webpackChunkName: "locale-mn" */ 'date-fns/locale/mn'
				);
				break;
			case 'km': // Khmer
				localeModule = await import(
					/* webpackChunkName: "locale-km" */ 'date-fns/locale/km'
				);
				break;
			case 'ckb': // Central Kurdish
				localeModule = await import(
					/* webpackChunkName: "locale-ckb" */ 'date-fns/locale/ckb'
				);
				break;
			case 'ug': // Uyghur
				localeModule = await import(
					/* webpackChunkName: "locale-ug" */ 'date-fns/locale/ug'
				);
				break;
			default:
				throw new Error(`Unsupported locale: ${localeName}`);
		}

		// date-fns locales export both a default export and a named export
		// We need to access the default export
		return (localeModule as any).default as Locale;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn(
			`Failed to load locale '${localeName}', falling back to en-US:`,
			error
		);
		return enUS;
	}
}

/**
 * Get the DayPicker locale based on WordPress settings
 *
 * This function dynamically loads the appropriate locale based on the WordPress
 * site's language setting. The locale is cached after first load.
 */
export async function getDayPickerLocale(): Promise<Locale> {
	// Get WordPress locale
	let wpLocale = getSettings().l10n?.locale || 'en_US';

	// Apply filter to allow customization
	if (window.gform?.applyFilters) {
		// Deprecated: use gpb_calendar_locale.
		wpLocale = window.gform.applyFilters(
			'gp_bookings_calendar_locale',
			wpLocale
		);
		wpLocale = window.gform.applyFilters('gpb_calendar_locale', wpLocale);
	}

	// Return cached locale if available
	if (localeCache.has(wpLocale)) {
		return localeCache.get(wpLocale)!;
	}

	// For en_US, return immediately (no need to import)
	if (wpLocale === 'en_US') {
		localeCache.set(wpLocale, enUS);
		return enUS;
	}

	// Get date-fns locale name and import
	const localeName = getDateFnsLocaleName(wpLocale);
	const locale = await importLocale(localeName);

	// Cache and return
	localeCache.set(wpLocale, locale);
	return locale;
}

/**
 * Get the default locale synchronously (always returns en-US)
 *
 * This is used as the initial locale while the actual locale is being loaded.
 *
 * @return The en-US locale object
 */
export function getDefaultLocale(): Locale {
	return enUS;
}
